import { createSelector } from 'reselect';

import { menu, payment, store, user } from '../../../core/state';
import { utils } from '../../../utils';
import {
  GatewayPaymentAccount,
  PickupLocationType,
  TableServiceCategory,
} from '../../Api';
import { Item as OldBasketItem } from '../../BasketControl';
import { BLANK } from '../../constants/Constants';
import * as oldBasketSelectors from '../../selectors/basketSelectors';
import * as ServiceChargeStates from '../../ServiceCharge';
import * as VoucherStates from '../../Vouchers';
import * as menuHelpers from '../menu/helpers';
import { defaultBasketState } from './reducers';

const getMenuUnNormalized = (state: WebOrder.State) => {
  const restaurant = getRestaurantSummary(state);

  if (restaurant) {
    const props = {
      menuUrl: restaurant.MenuUrl,
    };
    const menuData = menu.selectors.getMenuByUrl(
      state.flipdish.MenuData,
      props,
    );

    if (menuData) {
      return menuData;
    }
  }

  return undefined;
};

const getMenu = createSelector(
  [getMenuUnNormalized],
  (unNormilized): ReturnType<typeof menuHelpers.normalizeMenu> => {
    if (!unNormilized) {
      return undefined;
    }

    // ICH+29.08.2022 - what exactly is this normalization process doing, and can it be optimised
    const normalized = menuHelpers.normalizeMenu(unNormilized);
    return normalized;
  },
);

const isRequestedTimeValid = (state: WebOrder.State): boolean => {
  const details = getRestaurantDetails(state);
  const selection = getSelectedRequestedTime(state);

  if (
    details &&
    details.AcceptPreOrders &&
    // !details.IsOpen && // INVESTIGATE should PreOrders only be for closed stores
    details.PreOrderOptions &&
    details.PreOrderOptions.RequireExplicitSelect
  ) {
    return !(!selection || selection === BLANK);
  }
  return true;
};

const isUserNameValid = (state: WebOrder.State) => {
  const details = getRestaurantDetails(state);

  if (details && details.RequireCustomerName) {
    const userData = user.selectors.getUserConfig(state);
    return userData && !!userData.CustomerName;
  }

  return true;
};

// ========================

export const getBasket = (state: WebOrder.State) =>
  state.basket || defaultBasketState;

export const getBasketItems = (state: WebOrder.State) => getBasket(state).items;

export const getLocationParams = (state: WebOrder.State) =>
  state?.location?.params || {};

export const getVrId = (state: WebOrder.State) => getLocationParams(state).vrid;

export const getDeliveryType = (state: WebOrder.State) =>
  getLocationParams(state).type;

export const getPrId = (state: WebOrder.State) => {
  if (getLocationParams(state).type) {
    if (getLocationParams(state).type === 'collection') {
      return getLocationParams(state).id;
    }
  }
  return undefined;
};

export const getDlId = (state: WebOrder.State) => {
  if (getLocationParams(state).type) {
    if (getLocationParams(state).type === 'delivery') {
      return getLocationParams(state).id;
    }
  }
  return undefined;
};

export const getRouteProps = (state: WebOrder.State) => {
  const vrId = getVrId(state);
  const prId = getPrId(state);
  const deliveryType = getDeliveryType(state);
  const dlId = getDlId(state);

  if (!deliveryType || !vrId) {
    return undefined;
  }

  if (deliveryType === 'collection' && !prId) {
    return undefined;
  }
  if (deliveryType === 'delivery' && !dlId) {
    return undefined;
  }

  return {
    vrId: utils.change.stringToNumber(vrId),
    prId: utils.change.stringToNumber(prId),
    deliveryType,
    deliveryLocationId: utils.change.stringToNumber(dlId),
  };
};

export const getRoutePropsWithMenuId = (state: WebOrder.State) => {
  const props = getRouteProps(state);
  if (!props) {
    return undefined;
  }

  const menuData = getMenu(state);
  if (!menuData) {
    return undefined;
  }

  return {
    ...props,
    menuId: menuData.menuId,
    menuVersionGuid: menuData.menus[menuData.menuId].VersionGuid,
  };
};

export const getRestaurantSummary = (state: WebOrder.State) => {
  const props = getRouteProps(state);
  if (!props) {
    return undefined;
  }

  return store.selectors.getRestaurantSummary(state, props);
};

export const getRestaurantDetails = (state: WebOrder.State) => {
  const props = getRouteProps(state);
  if (!props) {
    return undefined;
  }

  return store.selectors.getRestaurantDetails(state, props);
};

export const getChefNotes = (state: WebOrder.State) =>
  state.basket.chefNote || '';

export const getExcludeServiceCharge = (state: WebOrder.State) =>
  state.basket.excludeServiceCharge;

export const getTableNumber = (state: WebOrder.State) =>
  state.basket.tableNumber;

export const getItemTotal = createSelector(
  [getBasketItems, getMenu],
  (basketItems, menuData): number => {
    if (!menuData) {
      return 0;
    }

    const total = Object.values(basketItems).reduce((sum, basketItem) => {
      const menuItem = menuData.items[basketItem.itemId];
      if (!menuItem) {
        return 0;
      }

      const optionsTotal = basketItem.options.reduce((sumOptions, optionId) => {
        const optionSetItem = menuData.optionSetItems[optionId];
        return sumOptions + optionSetItem.Price * basketItem.count;
      }, 0);

      return sum + menuItem.Price * basketItem.count + optionsTotal;
    }, 0);

    return total;
  },
);

export const getSelectedRequestedTime = (state: WebOrder.State) =>
  getBasket(state).requestedTime;

export const getSelectedRequestedTimeOrDefault = createSelector(
  [getRestaurantDetails, getSelectedRequestedTime],
  (details, selectedRequestedTime) => {
    if (!details || !details.AcceptPreOrders || !details.PreOrderOptions) {
      return '';
    }
    const explicit = details.PreOrderOptions.RequireExplicitSelect;
    const preOrderTimes = details.PreOrderOptions.PreOrderTimes;

    if (!explicit && !selectedRequestedTime) {
      const availablePreOrderTime = preOrderTimes.find((t) => !t.IsFull);
      if (availablePreOrderTime) {
        return availablePreOrderTime.Key;
      }
    }

    return !selectedRequestedTime ? BLANK : selectedRequestedTime;
  },
);

// #region OBSOLETE

/** (TEMPORARY) one way bridge from old actions and new sagas world */
export const getBasketItemsBridge = createSelector(
  [(state: WebOrder.State) => getBasket(state).items, getMenu],
  (items, menuData): OldBasketItem[] => {
    if (!menuData) {
      return [];
    }

    return Object.values(items)
      .sort((a, b) => a.order - b.order)
      .reduce((agg, item) => {
        // validation: check if item still exists
        if (!menuData.items[item.itemId]) {
          return agg;
        }

        const Item: Flipdish.MenuJson.MenuItem = {
          ...menuData.items[item.itemId],
          ConcessionStoreId: item.ConcessionStoreId,
          MenuItemOptionSets: menuData.items[
            item.itemId
          ].MenuItemOptionSets.map((osId) => ({
            ...menuData.optionSets[osId],
            MenuItemOptionSetItems: menuData.optionSets[
              osId
            ].MenuItemOptionSetItems.map(
              (osiId) => menuData.optionSetItems[osiId],
            ),
          })),
        };

        const Options: Flipdish.MenuJson.MenuItemOptionSetItem[] =
          item.options.map((oId) => menuData.optionSetItems[oId]);

        // validation: check if all options still exists
        if (Options.some((o) => !o)) {
          return agg;
        }

        for (let index = 0; index < item.count; index += 1) {
          agg.push({
            Item,
            Options,
          });
        }
        return agg;
      }, []);
  },
);

export const getVoucherBridge = createSelector(
  [
    (state: WebOrder.State) => state.basket.orderLoading,
    (state: WebOrder.State) => state.basket.voucher,
  ],
  (orderLoading, voucher): VoucherStates.States => {
    if (!voucher) {
      if (orderLoading) {
        return {
          status: 'loading',
          code: '',
        } as VoucherStates.Loading;
      }
      return {
        status: 'none',
        code: '',
      } as VoucherStates.None;
    }

    if (orderLoading) {
      return {
        status: 'loading',
        code: voucher.code,
        items: [
          {
            Name: voucher.code,
            Description: voucher.description,
            Amount: voucher.amount,
            TaxAmount: voucher.taxAmount,
          },
        ],
        isUserInput: voucher.isUserInput,
      } as VoucherStates.Loading;
    }

    return {
      code: voucher.code,
      isUserInput: voucher.isUserInput,
      items: [
        {
          Name: voucher.code,
          Description: voucher.description,
          Amount: voucher.amount,
          TaxAmount: voucher.taxAmount,
        },
      ],
      status: 'valid',
    } as VoucherStates.Valid;
  },
);

export const getServiceChargeBridge = createSelector(
  [
    (state: WebOrder.State) => state.basket.orderLoading,
    (state: WebOrder.State) => state.basket.serviceCharge,
  ],
  (orderLoading, serviceCharge): ServiceChargeStates.States => {
    if (!serviceCharge) {
      if (orderLoading) {
        return {
          status: 'loading',
        } as ServiceChargeStates.Loading;
      }
      return {
        status: 'none',
      } as ServiceChargeStates.None;
    }

    if (orderLoading) {
      return {
        status: 'loading',
        config: serviceCharge,
      } as ServiceChargeStates.Loading;
    }

    return {
      status: 'valid',
      config: serviceCharge,
    } as ServiceChargeStates.Valid;
  },
);

export const getBasketBridge = createSelector(
  [
    getMenu,
    getBasketItemsBridge,
    getSelectedRequestedTime,
    getVoucherBridge,
    getServiceChargeBridge,
    getExcludeServiceCharge,
    getTableNumber,
  ],
  (
    menuData,
    items,
    requestedTime,
    voucher,
    serviceCharge,
    excludeServiceCharge,
    tableNumber,
  ) => {
    if (!menuData) {
      return {
        excludeServiceCharge,
        items: [],
        menuId: 0,
        requestedTime: '',
        serviceCharge,
        tableNumber: null,
        voucher,
      };
    }

    return {
      excludeServiceCharge,
      items,
      menuId: menuData.menuId,
      requestedTime,
      serviceCharge,
      tableNumber,
      voucher,
    };
  },
);

export const isPickupLocationIdValid = (state: WebOrder.State) => {
  const { pickupLocation, tableServiceCategory, tableNumber } = state.basket;

  if (
    pickupLocation === PickupLocationType.TableService &&
    tableServiceCategory !== TableServiceCategory.DriveThrough &&
    !tableNumber
  ) {
    return false;
  }

  return true;
};

export const getCheckoutErrors = createSelector(
  [isRequestedTimeValid, isUserNameValid, isPickupLocationIdValid],
  (requestedTimeValid, userNameValid, pickupLocationIdValid) => {
    const errors = [] as WebOrder.State.BasketValidationError[];

    if (!requestedTimeValid) {
      errors.push('requested_time');
    }
    if (!userNameValid) {
      errors.push('name');
    }
    if (!pickupLocationIdValid) {
      errors.push('table_number');
    }

    return errors;
  },
);

/**
 * Gets Pickup location type
 * 1. user selection (with validation)
 * 2. default is TakeOut or first in Restaurant Summary options
 */
export const getPickupLocationTypeOrDefault = createSelector(
  [
    getRestaurantSummary,
    (state: WebOrder.State) => state.basket.pickupLocation,
  ],
  (Restaurant, selectedPickupLocationType) => {
    if (
      !Restaurant ||
      !('PickupLocationTypes' in Restaurant) ||
      ('PickupLocationTypes' in Restaurant &&
        !Restaurant.PickupLocationTypes.length)
    ) {
      return undefined;
    }

    const { PickupLocationTypes } = Restaurant;

    if (PickupLocationTypes.includes(selectedPickupLocationType)) {
      return selectedPickupLocationType;
    }
    if (PickupLocationTypes.includes(PickupLocationType.TakeOut)) {
      return PickupLocationType.TakeOut;
    }
    return PickupLocationTypes[0];
  },
);

/**
 * Gets Pickup LocationId (aka table number)
 */
export const getPickupLocationId = (state: WebOrder.State) =>
  state.basket.tableNumber;

/**
 * Gets Pickup LocationId only if user selected 'TableService' and input table number
 */
export const getPickupLocationIdOrDefault = createSelector(
  [getPickupLocationTypeOrDefault, getPickupLocationId],
  (pickupLocationType, selectedPickupLocationId) => {
    if (pickupLocationType === PickupLocationType.TableService) {
      return selectedPickupLocationId;
    }

    return undefined;
  },
);

// #endregion

// #region WIP TRANSITION

export const getRoundUpAmount = createSelector(
  [(state: WebOrder.State) => getTotalToPay(state)],
  (totalToPay) => {
    const roundUpAmount = Math.ceil(totalToPay) - totalToPay;
    return Math.round(roundUpAmount * 100) / 100;
  },
);

export const getTotal = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return 0;
  }

  return oldBasketSelectors.getTotal(state, props);
};

export const getTotalToPay = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return 0;
  }

  const totalToPay = oldBasketSelectors.getTotalToPay(state, props);
  return totalToPay;
};

export const getTipTotal = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return 0;
  }
  return oldBasketSelectors.getTipTotal(state, props);
};

export const getTaxTotal = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return 0;
  }
  return oldBasketSelectors.getItemTaxTotal(state);
};

export const getBasketValidationError = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return undefined;
  }
  return oldBasketSelectors.getBasketValidationError(state, props);
};

export const getOrderPayload = (state: WebOrder.State) => {
  const props = getRoutePropsWithMenuId(state);
  if (!props) {
    return undefined;
  }
  return oldBasketSelectors.getOrderPayload(state, props);
};

export const getCreatedOrders = (state: WebOrder.State) =>
  state.basket.createdOrders;

export const getSubmittedOrder = (state: WebOrder.State) =>
  state.basket.submittedOrder;

export const getSubmittedOrderCancelTimeout = (state: WebOrder.State) =>
  (state.basket.submittedOrder &&
    state.basket.submittedOrder.CancelOrderPeriodSeconds) ||
  0;

/**
 * Gets from available payments (user and restaurant) selected or default
 * 1. user selection
 * 2. user default
 * 3. cash
 * 4. card
 * 5. stripe
 * 6. paypal saved account
 * 7. ideal
 * 8. bancontact
 */
export const getSelectedPaymentIdOrDefault = createSelector(
  [
    (state: WebOrder.State) => payment.selectors.getSelectedPaymentId(state),
    (state: WebOrder.State) =>
      payment.selectors.getAllAvailablePaymentsMethods(state),
  ],
  (selectedPaymentId, availablePayments) => {
    if (!availablePayments.length) {
      return undefined;
    }

    // 1. user selection
    const isPaymentSelected =
      selectedPaymentId &&
      availablePayments.find((p) => p.paymentId === selectedPaymentId);
    if (isPaymentSelected) {
      return selectedPaymentId;
    }

    // 2. user default
    const defaultPaymentMethod = availablePayments.find(
      (p) => p?.IsDefaultPaymentMethod,
    );
    if (defaultPaymentMethod) {
      return defaultPaymentMethod.paymentId;
    }

    // TODO refactor all this weird stuff coz it will go on and on...
    // 3. cash
    const defCash = availablePayments.find(
      (p) => p.PaymentAccountType === 'CashPaymentAccount',
    );
    if (defCash) {
      return defCash.paymentId;
    }
    // 4. card
    const defCard = availablePayments.find(
      (p) =>
        p.PaymentAccountType === 'CardPaymentAccount' &&
        p.paymentId !== 'new_card',
    );
    if (defCard) {
      return defCard.paymentId;
    }
    // 5. stripe
    if (
      availablePayments.find(
        (p) => p.paymentId === 'stripe' && p.status === 'success',
      )
    ) {
      return 'stripe';
    }
    // 6. paypal saved account
    const defPayPal = availablePayments.find(
      (p) =>
        p.PaymentAccountType === 'BrainTreePayPalPaymentAccount' &&
        p.paymentId !== 'paypal',
    );
    if (defPayPal) {
      return defPayPal.paymentId;
    }
    // 7. ideal
    const defIdeal = availablePayments.find(
      (p) => p.PaymentAccountType === GatewayPaymentAccount.Ideal,
    );
    if (defIdeal) {
      return defIdeal.paymentId;
    }
    // 8. bancontact
    const defBanc = availablePayments.find(
      (p) => p.PaymentAccountType === GatewayPaymentAccount.Bancontact,
    );
    if (defBanc) {
      return defBanc.paymentId;
    }

    return availablePayments[0].paymentId;
  },
);

export const getSelectedPaymentOrDefault = (state: WebOrder.State) => {
  const paymentAccountId = getSelectedPaymentIdOrDefault(state);

  if (paymentAccountId) {
    const selectedPayment = state.payments.items[paymentAccountId];
    if (selectedPayment) {
      return selectedPayment;
    }

    const selectedRestaurantVoucherPayment =
      payment.selectors.getSelectedRestaurantVoucherPayment(state);
    if (selectedRestaurantVoucherPayment) {
      return selectedRestaurantVoucherPayment;
    }
  }

  return undefined;
};

export const getStripePaymentRequestOptions = (
  state: WebOrder.State,
): Stripe.PaymentRequestOptions => {
  const rSummary = getRestaurantSummary(state);
  if (!rSummary) {
    return undefined;
  }

  const currencyIso = rSummary.IsoCurrency;
  const restaurantName = rSummary.PhysicalRestaurantName;

  // KNOWN BUG - backend need to return countryCode based on Stripe account
  // let CountryCode = state.flipdish.CountryCode;
  // if (!CountryCode) {
  //   CountryCode = TimezoneIdToCountryCode(rSummary.TimeZoneInfoId);
  // }

  if (!currencyIso) {
    return undefined;
  }

  return {
    // https://stripe.com/docs/js/payment_request/create
    // The two-letter country code of your Stripe account
    country: 'IE',
    currency: currencyIso.toLowerCase(),
    total: {
      amount: 0,
      label: restaurantName,
    },
    // TODO should be nice print w options and sum
    // displayItems: orderPayload.OrderItemVms.map(iId => {
    //   const mItem = menu.items[iId];
    //   return { amount: mItem.Price * 100, label: mItem.Name };
    // })
  };
};

export const getStripePaymentRequestUpdateOptions = createSelector(
  [getTotalToPay, getRestaurantSummary],
  (totalToPay, restaurant): Stripe.PaymentRequestUpdateOptions => {
    const currencyIso = restaurant.IsoCurrency;
    const restaurantName = restaurant.PhysicalRestaurantName;

    return {
      currency: currencyIso.toLowerCase(),
      // WE need Math.round here cause 36.95 * 100 = 3695.000005 and this is causing problems with (stripe) apple/google pay
      total: {
        amount: Math.round(totalToPay * 100),
        label: restaurantName,
      },
      // TODO should be nice print w options and sum
      // displayItems: orderPayload.OrderItemVms.map(iId => {
      //   const mItem = menu.items[iId];
      //   return { amount: mItem.Price * 100, label: mItem.Name };
      // })
    };
  },
);
